import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, Bell, Palette, Globe, Key, Shield, Save, Loader2, Volume2, Mic } from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { ThemeSelector } from '../components/ui/ThemeSelector';
import { VoiceSettingsPanel } from '../components/ui/VoiceSettingsPanel';
import LanguageSelector from '../components/LanguageSelector';
import AlwaysListeningSettings from '../components/AlwaysListeningSettings';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(true);
  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [saving, setSaving] = useState(false);

  const saveSettings = async () => {
    setSaving(true);
    // Simulate saving
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Settings saved');
    setSaving(false);
  };

  const SettingRow = ({ icon: Icon, title, description, children }) => (
    <div className="flex items-center justify-between py-4 border-b border-[var(--border-color)] last:border-0">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-[var(--bg-card)] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[var(--text-secondary)]" />
        </div>
        <div>
          <h4 className="font-medium text-[var(--text-primary)]">{title}</h4>
          <p className="text-sm text-[var(--text-tertiary)]">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );

  const Toggle = ({ enabled, onChange }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-12 h-6 rounded-full p-1 transition-colors ${
        enabled ? 'bg-[var(--color-primary)]' : 'bg-[var(--bg-tertiary)]'
      }`}
    >
      <motion.div
        animate={{ x: enabled ? 24 : 0 }}
        className="w-4 h-4 rounded-full bg-white"
      />
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="settings-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2 text-[var(--text-primary)]">Settings</h1>
        <p className="text-[var(--text-secondary)]">Customize your AIRA experience</p>
      </div>

      {/* Theme Mode */}
      <GlassCard className="p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
          <Palette className="w-5 h-5 text-[var(--color-primary)]" />
          Appearance
        </h3>
        <ThemeSelector />
      </GlassCard>

      {/* Language Settings */}
      <div className="mb-6">
        <LanguageSelector />
      </div>

      {/* Always Listening Mode */}
      <div className="mb-6">
        <AlwaysListeningSettings />
      </div>

      {/* Voice Settings */}
      <GlassCard className="p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
          <Volume2 className="w-5 h-5 text-[var(--color-primary)]" />
          Voice Settings
        </h3>
        <VoiceSettingsPanel />
      </GlassCard>

      {/* Preferences */}
      <GlassCard className="p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
          <SettingsIcon className="w-5 h-5 text-[var(--color-primary)]" />
          Preferences
        </h3>

        <SettingRow
          icon={Bell}
          title="Notifications"
          description="Receive updates and reminders"
        >
          <Toggle enabled={notifications} onChange={setNotifications} />
        </SettingRow>
      </GlassCard>

      {/* API Keys */}
      <GlassCard className="p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
          <Key className="w-5 h-5 text-[var(--color-primary)]" />
          API Configuration
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">ElevenLabs API Key (for Voice Agent)</label>
            <input
              type="password"
              value={elevenLabsKey}
              onChange={(e) => setElevenLabsKey(e.target.value)}
              placeholder="Enter your ElevenLabs API key"
              className="input-field w-full"
              data-testid="elevenlabs-key-input"
            />
            <p className="text-xs text-[var(--text-tertiary)] mt-1">
              Get your key from{' '}
              <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] hover:underline">
                elevenlabs.io
              </a>
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Security */}
      <GlassCard className="p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--text-primary)]">
          <Shield className="w-5 h-5 text-[var(--color-primary)]" />
          Security
        </h3>

        <div className="space-y-4">
          <button className="btn-secondary w-full">Change Password</button>
          <button className="w-full py-3 px-6 border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-full hover:bg-[var(--color-error)]/10 transition-colors">
            Delete Account
          </button>
        </div>
      </GlassCard>

      {/* Save Button */}
      <button
        onClick={saveSettings}
        disabled={saving}
        className="btn-primary w-full flex items-center justify-center gap-2"
        data-testid="save-settings-btn"
      >
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Save className="w-5 h-5" />
            Save Settings
          </>
        )}
      </button>
    </div>
  );
}
